import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import SpreadsheetForm from './Form/SpreadsheetForm';
import GoogleAuthForm from './Form/GoogleAuthForm';
import SheetMappingForm from './Form/SheetMappingForm';
import CustomReportForm from './Form/CustomReportForm/index.jsx';
import CustomReportFormSkeleton from './Skeleton/CustomReportFormSkeleton';
import SheetMappingFormSkeleton from './Skeleton/SheetMappingFormSkeleton';
import SpreadsheetFormSkeleton from './Skeleton/SpreadsheetFormSkeleton';
import * as Resource from '../../apis/SpreadsheetReportConfigs';
import { convertCamelCaseKeys } from '../../lib/objectUtils';
import { connectSpreadsheetPath } from '../../routes/SpreadsheetReportConfigs';
import {
  STATUS_VALUES,
  DEFAULT_SPREADSHEET_REPORT_CONFIGS,
  OTHER_COLUMN_VALUES,
  SHOW_HEADER_ROW_OPTION_VALUES,
} from '../../constants/SpreadsheetReportConfigs';
import * as BS from 'react-bootstrap';
import {
  editPagePath,
  listPagePath,
  newPagePath,
} from '../../routes/SpreadsheetReportConfigs';
import {
  removeTurbolinksHistory,
  removeUrlParams,
  updateQueryParams,
} from '../../lib/UrlUtils';
import { convertSnakeCaseKeys } from '../../lib/RESTAPI';
import { useHistoryStepNavigation } from '../../hooks';
import ConversionPoint from '../../models/SpreadsheetReportConfigs/ConversionPoint';
import {
  createGooglePickerObject,
  loadGoogleScript,
} from '../../lib/GoogleScript';

const scope = 'frontend.components.SpreadsheetReportConfigs';
const OPEN_PICKER_QUERY_KEY = 'open_picker';
const PICKER_SESSION_FLAG_KEY = 'spreadsheetPickerAutoOpen';

const propTypes = {
  projectId: PropTypes.string,
  spreadsheetReportConfigId: PropTypes.string,
  initialSpreadsheetId: PropTypes.string,
  stepParam: PropTypes.string,
  isEdit: PropTypes.bool,
  initialIsRenewConnect: PropTypes.bool,
  isReauthenticate: PropTypes.bool,
  copiedSpreadsheetConfigId: PropTypes.string,
  conversionPoints: PropTypes.arrayOf(
    PropTypes.shape(ConversionPoint.propTypes)
  ),
  limitReached: PropTypes.bool,
  channelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

const Form = ({
  projectId,
  spreadsheetReportConfigId,
  initialSpreadsheetId,
  stepParam,
  isEdit,
  initialIsRenewConnect,
  isReauthenticate,
  copiedSpreadsheetConfigId,
  conversionPoints,
  limitReached,
  channelOptions = [],
}) => {
  const initialStep = stepParam || (isEdit ? 'customReport' : 'googleAuth');
  const [step, setStep] = useState(initialStep); // 'googleAuth' | 'spreadsheet' | 'sheetMapping' | 'customReport'
  const [sheetList, setSheetList] = useState([]);
  const [sheetMapping, setSheetMapping] = useState(null);
  const [savedSpreadsheetReportConfigs, setSavedSpreadsheetReportConfigs] =
    useState(null);
  const [spreadsheetId, setSpreadsheetId] = useState(initialSpreadsheetId);
  const [isRenewConnect, setIsRenewConnect] = useState(
    initialIsRenewConnect ?? false
  );
  const [spreadsheetReportConfigs, setSpreadsheetReportConfigs] = useState({
    ...structuredClone(DEFAULT_SPREADSHEET_REPORT_CONFIGS),
    ...{ sheetId: !isEdit ? spreadsheetId || '' : '' },
  });
  const [httpResponseError, setHttpResponseError] = useState(null);
  const getInitialApiFetchingList = () => {
    if (initialStep === 'spreadsheet') return ['googleProfile'];
    if (isEdit || copiedSpreadsheetConfigId) return ['spreadsheetReportConfig'];
    if (initialSpreadsheetId) return ['sheetList'];
    return [];
  };
  const [apiFetchingList, setApiFetchingList] = useState(
    getInitialApiFetchingList
  );
  const [googleProfile, setGoogleProfile] = useState(null);
  const [importSpreadsheetReportConfigs, setImportSpreadsheetReportConfigs] =
    useState(null);
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);
  const [shouldAutoOpenPicker, setShouldAutoOpenPicker] = useState(() => {
    if (typeof window === 'undefined') return false;
    const sessionFlag = window.sessionStorage.getItem(PICKER_SESSION_FLAG_KEY);
    if (sessionFlag === '1') {
      window.sessionStorage.removeItem(PICKER_SESSION_FLAG_KEY);
      return true;
    }
    return false;
  });
  const [isOpeningPicker, setIsOpeningPicker] = useState(false);

  const conversionPointValues = useMemo(() => {
    return new Set((conversionPoints || []).map((point) => point.value));
  }, [conversionPoints]);
  const CONVERSION_POINT_COLUMN_PREFIXES = useMemo(
    () => ['cvr_', 'conversions_', 'cpa_', 'conversion_value_', 'roas_'],
    []
  );

  const escapeHtml = (unsafe) => {
    if (!unsafe) return '';
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  useHistoryStepNavigation(step, setStep);

  useEffect(() => {
    removeTurbolinksHistory();
    if (stepParam) {
      setStep(stepParam);
    } else {
      isEdit ? goToCustomReport({}, true) : goToGoogleAuth({}, true);
    }
  }, [stepParam, isEdit]);

  useEffect(() => {
    if (spreadsheetId) return;
    const successAlerts = document.querySelectorAll('.alert.alert-success');
    successAlerts.forEach((alert) => {
      alert.style.display = 'none';
    });
  }, [spreadsheetId]);

  useEffect(() => {
    if (step !== 'spreadsheet') return;
    setSheetMapping(null);
    fetchGoogleProfile();
  }, [step]);

  useEffect(() => {
    if (step === 'spreadsheet' || !apiFetchingList.includes('sheetList'))
      return;
    if (
      (!isEdit && spreadsheetId) ||
      (isEdit && spreadsheetId && spreadsheetReportConfigs.sheetId)
    ) {
      fetchSheetList();
    }
  }, [
    spreadsheetId,
    spreadsheetReportConfigId,
    spreadsheetReportConfigs.sheetId,
    step,
  ]);

  useEffect(() => {
    if (step === 'googleAuth') return;
    if (spreadsheetReportConfigId || copiedSpreadsheetConfigId) {
      fetchSpreadsheetReportConfig();
    }
  }, [spreadsheetReportConfigId, copiedSpreadsheetConfigId]);

  useEffect(() => {
    if (!shouldAutoOpenPicker || step !== 'customReport') return;
    if (isOpeningPicker) return;

    let isUnmounted = false;

    const openPicker = async () => {
      setIsOpeningPicker(true);
      removeUrlParams([OPEN_PICKER_QUERY_KEY], true);

      try {
        await loadGoogleScript();
        if (isUnmounted) return;
        setIsGoogleScriptLoaded(true);

        if (!googleProfile && !apiFetchingList.includes('googleProfile')) {
          setApiFetchingList((prevState) =>
            prevState.includes('googleProfile')
              ? prevState
              : [...prevState, 'googleProfile']
          );
        }

        let currentGoogleProfile =
          googleProfile || (await fetchGoogleProfile());

        if (isUnmounted) return;

        if (!currentGoogleProfile) {
          setShouldAutoOpenPicker(false);
          goToGoogleAuth({}, true);
          return;
        }

        if (currentGoogleProfile.expiresAt < Date.now()) {
          const refreshedProfile = await handleRefreshAccessToken();
          if (refreshedProfile) {
            currentGoogleProfile = refreshedProfile;
          }
        }

        if (isUnmounted) return;

        if (!window.google?.picker) {
          // Retry once after a short delay in case the picker namespace is still loading.
          setTimeout(() => {
            if (window.google?.picker) {
              createGooglePickerObject(
                currentGoogleProfile,
                handleSelectSpreadsheetFromPicker
              );
            }
          }, 150);
          setShouldAutoOpenPicker(false);
          return;
        }

        createGooglePickerObject(
          currentGoogleProfile,
          handleSelectSpreadsheetFromPicker
        );
        setShouldAutoOpenPicker(false);
      } catch (error) {
        setShouldAutoOpenPicker(false);
      } finally {
        if (!isUnmounted) {
          setIsOpeningPicker(false);
        }
      }
    };

    openPicker();

    return () => {
      isUnmounted = true;
    };
  }, [shouldAutoOpenPicker, step]);

  const goToGoogleAuth = (additionalQueryParams = {}, isReplaceState) => {
    setStep('googleAuth');
    updateQueryParams(
      { ...additionalQueryParams, step: 'googleAuth' },
      isReplaceState
    );
  };
  const goToSheetMapping = (additionalQueryParams = {}, isReplaceState) => {
    setStep('sheetMapping');
    updateQueryParams(
      { ...additionalQueryParams, step: 'sheetMapping' },
      isReplaceState
    );
  };
  const goToImportSheetMapping = (
    additionalQueryParams = {},
    isReplaceState
  ) => {
    setStep('importSheetMapping');
    updateQueryParams(
      { ...additionalQueryParams, step: 'importSheetMapping' },
      isReplaceState
    );
  };
  const goToCustomReport = (additionalQueryParams = {}, isReplaceState) => {
    setStep('customReport');
    updateQueryParams(
      { ...additionalQueryParams, step: 'customReport' },
      isReplaceState
    );
  };

  const removeApiFetchingItem = (doneItem) => {
    setApiFetchingList((prevState) =>
      prevState.filter((item) => item !== doneItem)
    );
  };

  const fetchGoogleProfile = async () => {
    return Resource.getGoogleProfile(projectId)
      .then((response) => {
        if (response.error) {
          if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(PICKER_SESSION_FLAG_KEY);
          }
          return null;
        }
        const convertedCamelCaseData = convertCamelCaseKeys(response.data);
        setGoogleProfile(convertedCamelCaseData);
        if (
          typeof window !== 'undefined' &&
          window.sessionStorage.getItem(PICKER_SESSION_FLAG_KEY) === '1'
        ) {
          window.sessionStorage.removeItem(PICKER_SESSION_FLAG_KEY);
          setShouldAutoOpenPicker(true);
        }
        return convertedCamelCaseData;
      })
      .finally(() => {
        removeApiFetchingItem('googleProfile');
      });
  };

  const fetchSheetList = () => {
    const getSheetListPromise =
      (isEdit && !isRenewConnect && !isReauthenticate) ||
      (copiedSpreadsheetConfigId && !isRenewConnect && !isReauthenticate)
        ? Resource.getSheetListBySpreadsheetId(
            projectId,
            spreadsheetId,
            isEdit ? spreadsheetReportConfigId : copiedSpreadsheetConfigId
          )
        : Resource.getSheetListBySpreadsheetId(projectId, spreadsheetId);

    getSheetListPromise
      .then((response) => {
        if (response.error) {
          throw new Error(response.error);
        }

        const convertedCamelCaseData = convertCamelCaseKeys(response.data);

        if (isEdit) {
          const sheets = (convertedCamelCaseData.sheets || []).map((sheet) => ({
            title: sheet.title,
          }));

          setSheetList(sheets);
        } else {
          const sheets = (convertedCamelCaseData.sheets || []).map((sheet) => ({
            title: sheet.title,
          }));
          setSheetList(sheets);

          if (copiedSpreadsheetConfigId) {
            return;
          }

          const panelsItems = Object.fromEntries(
            sheets.map((sheet) => [sheet.title, []])
          );
          const sortedPanelKeys = Object.keys(panelsItems);

          setSpreadsheetReportConfigs((prevState) => ({
            ...structuredClone(prevState),
            panels: {
              items: panelsItems,
              sortedPanelKeys,
            },
          }));
        }
      })
      .catch(() => {
        if (isEdit) {
          const queryParamsString = new URLSearchParams({
            step: 'googleAuth',
            reauthenticate: true,
          });
          setHttpResponseError(
            I18n.t(`${scope}.messages.connectSpreadsheetError`, {
              redirectUrl: editPagePath(
                projectId,
                spreadsheetReportConfigId,
                queryParamsString
              ),
            })
          );
        } else {
          let queryParamsString = null;
          if (copiedSpreadsheetConfigId) {
            queryParamsString = new URLSearchParams({
              copied_spreadsheet_config_id: copiedSpreadsheetConfigId,
              reauthenticate: true,
            });
          }
          setHttpResponseError(
            I18n.t(`${scope}.messages.connectSpreadsheetError`, {
              redirectUrl: newPagePath(projectId, queryParamsString),
            })
          );
        }
      })
      .finally(() => {
        removeApiFetchingItem('sheetList');
      });
  };

  const fetchSpreadsheetReportConfig = () => {
    const sourceSpreadsheetReportConfigId = isEdit
      ? spreadsheetReportConfigId
      : copiedSpreadsheetConfigId;

    Resource.getDetails(projectId, sourceSpreadsheetReportConfigId)
      .then((response) => {
        if (response.error) {
          throw new Error(response.error);
        }

        const { panels, ...restData } = response.data;
        const convertedPanelsItems = {};
        if (panels && panels.items) {
          Object.entries(panels.items).forEach(([sheetTitle, panelArray]) => {
            convertedPanelsItems[sheetTitle] = convertCamelCaseKeys(panelArray);
          });
        }
        let convertedCamelCaseRootData = convertCamelCaseKeys(restData);

        if (!isEdit && copiedSpreadsheetConfigId) {
          const {
            id: _id,
            deletedAt: _deletedAt,
            createdAt: _createdAt,
            updatedAt: _updatedAt,
            lastOutputAt: _lastOutputAt,
            ...cleanedData
          } = convertedCamelCaseRootData;
          convertedCamelCaseRootData = cleanedData;
          convertedCamelCaseRootData.status = STATUS_VALUES.invalid;
        }

        const finalData = {
          ...convertedCamelCaseRootData,
          panels: {
            items: convertedPanelsItems,
            sortedPanelKeys: panels.sorted_panel_keys,
          },
        };

        if (isEdit && !spreadsheetId) {
          setSpreadsheetId(finalData.sheetId);
        }
        setSavedSpreadsheetReportConfigs(finalData);
        setSpreadsheetReportConfigs(finalData);
        const sortedPanelKeys = finalData.panels?.sortedPanelKeys || [];
        setSheetList(
          sortedPanelKeys.map((sheetTitle) => ({ title: sheetTitle }))
        );
      })
      .catch(() => {
        setHttpResponseError(
          I18n.t(`${scope}.messages.fetchSpreadsheetReportConfigError`, {
            redirectUrl: listPagePath(projectId, I18n.locale),
          })
        );
      })
      .finally(() => {
        removeApiFetchingItem('spreadsheetReportConfig');
      });
  };

  const handleSelectSpreadsheetFromPicker = (data) => {
    if (
      !window.google?.picker ||
      data?.[window.google.picker.Response.ACTION] !==
        window.google.picker.Action.PICKED
    ) {
      return;
    }
    const documents = data[window.google.picker.Response.DOCUMENTS] || [];
    const file = documents[0];
    const fileId = file?.[window.google.picker.Document.ID];

    if (fileId) {
      handleSpreadsheetFormSubmit(
        { sheetId: fileId },
        { stayOnCustomReport: true }
      );
    }
  };

  const handleRefreshAccessToken = () => {
    setApiFetchingList(['googleProfile']);
    return fetchGoogleProfile();
  };

  const handleSyncSpreadsheetReportConfigs = (data) => {
    if (!data) return;
    setSpreadsheetReportConfigs((prevState) => ({
      ...structuredClone(prevState),
      ...structuredClone(data),
    }));
  };

  const handleUpdateSpreadsheetButtonClick = () => {
    goToGoogleAuth();
  };

  const handleUpdateSheetButtonClick = (data) => {
    setApiFetchingList(['sheetList']);
    setSpreadsheetId(data.sheetId);
    setSpreadsheetReportConfigs((prevState) => ({
      ...structuredClone(prevState),
      ...structuredClone(data),
    }));
    goToSheetMapping();
  };

  const handleImportSpreadsheetConfigStart = (importedConfig) => {
    if (!importedConfig) return;
    const baseConfig =
      typeof structuredClone === 'function'
        ? structuredClone(importedConfig)
        : JSON.parse(JSON.stringify(importedConfig));
    const sanitizedPanels = sanitizeImportedPanels(baseConfig.panels);
    setImportSpreadsheetReportConfigs({
      ...baseConfig,
      panels: sanitizedPanels,
    });
    setSheetMapping(null);
    goToImportSheetMapping();
  };

  const sanitizeImportedPanels = (panels) => {
    if (!panels?.items) {
      return {
        items: {},
        sortedPanelKeys: panels?.sortedPanelKeys || [],
      };
    }
    const isConversionPointColumn = (content) => {
      if (!content) return false;
      if (conversionPointValues.has(content)) return true;
      return CONVERSION_POINT_COLUMN_PREFIXES.some((prefix) =>
        content.startsWith(prefix)
      );
    };

    const sanitizeColumns = (columns = []) => {
      return columns.map((column) =>
        isConversionPointColumn(column.content)
          ? {
              ...column,
              content: OTHER_COLUMN_VALUES.blank,
              name: column.name || 'blank',
            }
          : column
      );
    };

    const sanitizeFilters = (filters = []) => {
      const sanitized = filters
        .map((group = []) =>
          group.filter(
            (condition) => !isConversionPointColumn(condition.targetColumn)
          )
        )
        .filter((group) => group.length > 0);
      return sanitized.length > 0 ? sanitized : [];
    };

    const sanitizeSorts = (sorts = []) => {
      return sorts.filter(
        (sort) => !isConversionPointColumn(sort.targetColumn)
      );
    };

    const sanitizedItems = Object.fromEntries(
      Object.entries(panels.items).map(([sheetTitle, panelArray]) => [
        sheetTitle,
        panelArray.map((panel) => ({
          ...panel,
          showHeaderRow:
            panel.showHeaderRow ?? SHOW_HEADER_ROW_OPTION_VALUES.show,
          columns: sanitizeColumns(panel.columns),
          filters: sanitizeFilters(panel.filters),
          sorts: sanitizeSorts(panel.sorts),
        })),
      ])
    );
    return {
      items: sanitizedItems,
      sortedPanelKeys:
        panels.sortedPanelKeys || Object.keys(sanitizedItems || {}),
    };
  };

  const handleImportSheetMappingFormSubmit = (data) => {
    const importedPanels = data?.spreadsheetReportConfigs?.panels;
    if (!importedPanels) {
      setImportSpreadsheetReportConfigs(null);
      goToCustomReport();
      return;
    }
    const sanitizedPanels = sanitizeImportedPanels(importedPanels);
    const sheetMappingValues = data.sheetMapping || {};
    setSpreadsheetReportConfigs((prevState) => {
      const previousPanels = structuredClone(prevState?.panels || {});
      const sanitizedItems = sanitizedPanels.items || {};
      const sortedPanelKeys = sanitizedPanels.sortedPanelKeys || [];
      const mergedItems = sortedPanelKeys.reduce((acc, sheetTitle) => {
        if (sheetMappingValues[sheetTitle]) {
          acc[sheetTitle] = sanitizedItems[sheetTitle] || [];
        } else {
          acc[sheetTitle] = previousPanels.items?.[sheetTitle]
            ? structuredClone(previousPanels.items[sheetTitle])
            : [];
        }
        return acc;
      }, {});

      return {
        ...structuredClone(prevState),
        panels: {
          items: mergedItems,
          sortedPanelKeys,
        },
      };
    });
    setSheetMapping(sheetMappingValues);
    setImportSpreadsheetReportConfigs(null);
    goToCustomReport();
  };

  const handleCancelButtonClick = () => {
    window.location.href = listPagePath(projectId, I18n.locale);
  };

  const handleSpreadsheetFormSubmit = (data, options = {}) => {
    const { stayOnCustomReport = false } = options;
    setApiFetchingList(['sheetList']);
    setSpreadsheetId(data.sheetId);
    if (isEdit) {
      let queryParams = {
        spreadsheet_id: data.sheetId,
      };
      if (data.sheetId !== savedSpreadsheetReportConfigs.sheetId) {
        queryParams.reconnect = true;
        setIsRenewConnect(true);
        setSpreadsheetReportConfigs(() => ({
          ...structuredClone(savedSpreadsheetReportConfigs),
          sheetId: data.sheetId,
        }));
      } else {
        if (isRenewConnect) {
          removeUrlParams(['reconnect']);
          setIsRenewConnect(false);
        }
      }
      stayOnCustomReport
        ? goToCustomReport(queryParams)
        : goToSheetMapping(queryParams);
    } else {
      if (
        copiedSpreadsheetConfigId &&
        data.sheetId !== savedSpreadsheetReportConfigs.sheetId
      ) {
        const queryParams = {
          reconnect: true,
          spreadsheet_id: data.sheetId,
        };
        setIsRenewConnect(true);
        setSpreadsheetReportConfigs(() => ({
          ...structuredClone(savedSpreadsheetReportConfigs),
          sheetId: data.sheetId,
        }));
        stayOnCustomReport
          ? goToCustomReport(queryParams)
          : goToSheetMapping(queryParams);
      } else if (copiedSpreadsheetConfigId) {
        setSpreadsheetReportConfigs(() => ({
          ...structuredClone(savedSpreadsheetReportConfigs),
        }));
        stayOnCustomReport ? goToCustomReport() : goToSheetMapping();
      } else {
        const queryParams = {
          spreadsheet_id: data.sheetId,
        };
        setSpreadsheetReportConfigs(() => ({
          ...structuredClone(DEFAULT_SPREADSHEET_REPORT_CONFIGS),
          sheetId: data.sheetId,
        }));
        goToCustomReport(queryParams);
      }
    }
  };

  const handleGoogleLogin = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('step', 'customReport');
    url.searchParams.delete(OPEN_PICKER_QUERY_KEY);
    const returnUrl = url.toString();
    history.replaceState({}, '', returnUrl);
    setShouldAutoOpenPicker(false);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(PICKER_SESSION_FLAG_KEY, '1');
    }
    window.location.href = connectSpreadsheetPath(projectId, returnUrl);
  };

  const handleSheetMappingFormSubmit = async (data) => {
    const updatedSpreadsheetReportConfigs = {
      ...structuredClone(spreadsheetReportConfigs),
      ...structuredClone(data.spreadsheetReportConfigs),
    };
    setSpreadsheetReportConfigs(updatedSpreadsheetReportConfigs);
    setSheetMapping(data.sheetMapping);
    goToCustomReport();
  };

  const handleCustomReportFormSubmit = async (data) => {
    try {
      const { panels, ...restData } = {
        ...structuredClone(spreadsheetReportConfigs),
        ...structuredClone(data),
      };

      const snakeCaseData = convertSnakeCaseKeys(restData);

      const convertedPanelsItems = {};
      Object.entries(panels.items).forEach(([sheetTitle, panelArray]) => {
        convertedPanelsItems[sheetTitle] = convertSnakeCaseKeys(panelArray);
      });

      const updatedSpreadsheetReportConfigs = {
        ...snakeCaseData,
        panels: {
          items: convertedPanelsItems,
          sorted_panel_keys: panels.sortedPanelKeys,
        },
      };

      let response;
      if (isEdit) {
        response = await Resource.update(
          projectId,
          spreadsheetReportConfigId,
          updatedSpreadsheetReportConfigs
        );
      } else {
        response = await Resource.create(
          projectId,
          updatedSpreadsheetReportConfigs,
          copiedSpreadsheetConfigId && !isRenewConnect && !isReauthenticate
            ? +copiedSpreadsheetConfigId
            : undefined
        );
      }

      if (response.error || response.data.errors) {
        throw new Error(response.error || response.data.errors);
      }

      window.location.href = listPagePath(projectId, I18n.locale);
    } catch (error) {
      const detail =
        error?.message ||
        (error?.response?.data ? JSON.stringify(error.response.data) : '');
      const escapedDetail = detail ? escapeHtml(detail) : '';
      const baseMessage = I18n.t(
        `${scope}.messages.updateSpreadsheetReportConfigError`
      );
      setHttpResponseError(
        escapedDetail
          ? `${baseMessage}<br/><small>${escapedDetail}</small>`
          : baseMessage
      );
    }
  };

  const stepComponentMap = {
    googleAuth: {
      Component: GoogleAuthForm,
      props: {
        projectId,
        onGoogleLogin: handleGoogleLogin,
      },
    },
    spreadsheet: {
      Component: SpreadsheetForm,
      props: {
        googleProfile,
        spreadsheetReportConfigs,
        onGoogleLogin: handleGoogleLogin,
        onSubmit: handleSpreadsheetFormSubmit,
        onRefreshAccessToken: handleRefreshAccessToken,
      },
    },
    sheetMapping: {
      Component: SheetMappingForm,
      props: {
        apiFetchingList,
        spreadsheetReportConfigs: isRenewConnect
          ? savedSpreadsheetReportConfigs
          : spreadsheetReportConfigs,
        currentSheetList: sheetList,
        spreadsheetId,
        isRenewConnect,
        sheetMapping,
        onSubmit: handleSheetMappingFormSubmit,
      },
    },
    importSheetMapping: {
      Component: importSpreadsheetReportConfigs
        ? SheetMappingForm
        : SheetMappingFormSkeleton,
      props: importSpreadsheetReportConfigs
        ? {
            apiFetchingList,
            spreadsheetReportConfigs: importSpreadsheetReportConfigs,
            currentSheetList: sheetList,
            spreadsheetId,
            isRenewConnect,
            sheetMapping,
            onSubmit: handleImportSheetMappingFormSubmit,
          }
        : {},
    },
    customReport: {
      Component: CustomReportForm,
      props: {
        projectId,
        isEdit,
        spreadsheetReportConfigs,
        sheetList,
        conversionPoints,
        limitReached,
        channelOptions,
        onUpdateSpreadsheetButtonClick: handleUpdateSpreadsheetButtonClick,
        onUpdateSheetButtonClick: handleUpdateSheetButtonClick,
        onImportSpreadsheetConfigStart: handleImportSpreadsheetConfigStart,
        onSyncSpreadsheetReportConfigs: handleSyncSpreadsheetReportConfigs,
        onCancelButtonClick: handleCancelButtonClick,
        onSubmit: handleCustomReportFormSubmit,
      },
    },
  };

  const { Component, props } = stepComponentMap[step];
  const isBlockingLoading =
    step === 'customReport'
      ? apiFetchingList.includes('spreadsheetReportConfig')
      : apiFetchingList.length !== 0;

  return (
    <div className='row'>
      <div className='col-md-12'>
        {httpResponseError && (
          <BS.Alert bsStyle='danger'>
            <i className='fa fa-warning icon' />
            <span dangerouslySetInnerHTML={{ __html: httpResponseError }} />
          </BS.Alert>
        )}
        {isBlockingLoading && step === 'googleAuth' && !httpResponseError && (
          <SpreadsheetFormSkeleton />
        )}
        {isBlockingLoading && step === 'spreadsheet' && !httpResponseError && (
          <SpreadsheetFormSkeleton />
        )}
        {isBlockingLoading && step === 'customReport' && !httpResponseError && (
          <CustomReportFormSkeleton />
        )}
        {isBlockingLoading && step === 'sheetMapping' && !httpResponseError && (
          <SheetMappingFormSkeleton />
        )}
        {isBlockingLoading &&
          step === 'importSheetMapping' &&
          !httpResponseError && <SheetMappingFormSkeleton />}
        {!isBlockingLoading && !httpResponseError && <Component {...props} />}
      </div>
    </div>
  );
};

Form.propTypes = propTypes;

export default Form;
